import { SvgIconProps } from "@mui/material";
import { forwardRef, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import Icon from "@/components/icon";

import { InputWrapperStyleProps } from "./styles/input-wrapper.styles";
import { SelectInputStyleProps } from "./styles/select-input.styles";

interface SelectInputProps {
  error?: string;
  isValid?: boolean;
  required?: boolean;
  disabled?: boolean;
  dataCy?: string;
  placeholder?: string;
  children: React.ReactNode;
  wrapperStyles?: InputWrapperStyleProps;
  styles?: SelectInputStyleProps;
  rightIcon?: React.ComponentType<SvgIconProps>;
  [key: string]: any;
}

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(
  (
    {
      error,
      isValid,
      required,
      disabled,
      dataCy,
      placeholder,
      children,
      styles: _styles,
      rightIcon: _RightIcon,
      wrapperStyles: _wrapperStyles = {},
      value,
      ...props
    },
    ref,
  ) => {
    const [hasValue, setHasValue] = useState(!!value);

    useEffect(() => {
      setHasValue(!!value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setHasValue(!!e.target.value);
      if (props.onChange) {
        props.onChange(e);
      }
    };

    const componentId = `select-input-${Math.random().toString(36).substring(2, 11)}`;

    return (
      <>
        <div id={componentId} className="relative w-full">
          <select
            ref={ref}
            className={twMerge(
              "w-full bg-white autofill:bg-transparent rounded-md appearance-none mr-0 cursor-pointer",
              "border focus:outline-none focus:ring-0 pl-4 pr-10 h-12",
              "custom-select border-[#9b9b9b] hover:border-[#9b9b9b] focus:border-[#9b9b9b]",
              disabled && "cursor-not-allowed",
              error && "border-primary bg-danger3",
              hasValue && "has-value",
            )}
            disabled={disabled}
            data-cy={dataCy}
            required={required}
            value={value}
            onChange={handleChange}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {children}
          </select>

          <div className="flex items-center gap-2 absolute right-2 top-3">
            {error && <Icon icon="cross" size={25} className="text-primary" />}
            {isValid && !error && (
              <Icon icon="check-simple" size={25} className="text-success2" />
            )}
          </div>
        </div>

        <style jsx>{`
          #${componentId} .custom-select:not(.has-value) {
            color: #9b9b9b;
            font-style: italic;
          }
        `}</style>

        <style jsx global>{`
          .custom-select option {
            color: initial;
            font-style: normal;
          }
        `}</style>
      </>
    );
  },
);

SelectInput.displayName = "SelectInput";
